import React, { useContext, useCallback, useState, useRef, useEffect, useMemo } from 'react';

import {
  Button,
  Card,
  EmptyState,
  Label,
  ScrollableContainer,
  SearchInputToggle,
  Spinner,
  useClickOutside,
} from '@ergeon/core-components';
import { UserCircleIcon, UserGroupIcon, UserIcon, CheckIcon, ChevronDownIcon } from '@ergeon/icons/hero/micro';
import { UserCircleIcon as UserCircleOutlineIcon } from '@ergeon/icons/hero/outline';
import { debounce, noop } from 'lodash';

import { Staff } from '../../types';
import { StaffFilterContext } from '../../context-providers/StaffFilterContext';

const SEARCH_DEBOUNCE_MS = 300;

const StaffFilter: React.FC = () => {
  const {
    currentUser,
    selection,
    setSelection,
    isSelectionInitialized,
    staffList,
    isLoadingStaff,
    searchStaff,
    searchQuery,
    hasMoreStaff,
    loadMoreStaff,
  } = useContext(StaffFilterContext);

  const [isOpen, setIsOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerTargetRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useClickOutside(containerRef as React.RefObject<HTMLElement>, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query: string) => searchStaff(query), SEARCH_DEBOUNCE_MS),
    [searchStaff],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = useCallback(
    (value?: string) => {
      const newValue = value || '';
      setLocalSearchQuery(newValue);
      debouncedSearch(newValue);
    },
    [debouncedSearch],
  );

  const handleSelectEveryone = useCallback(() => {
    setSelection({ type: 'everyone' });
    setIsOpen(false);
  }, [setSelection]);

  const handleSelectCurrentUser = useCallback(() => {
    if (!currentUser) return;
    setSelection({ type: 'current_user', user: currentUser });
    setIsOpen(false);
  }, [currentUser, setSelection]);

  const handleSelectStaff = useCallback(
    (staff: Staff) => {
      setSelection({ type: 'specific_staff', staff });
      setIsOpen(false);
    },
    [setSelection],
  );

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreStaff && !isLoadingStaff) {
          loadMoreStaff();
        }
      },
      { threshold: 0.1 },
    );

    const target = observerTargetRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [isOpen, hasMoreStaff, isLoadingStaff, loadMoreStaff]);

  // Reset local search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setLocalSearchQuery(searchQuery);
    }
  }, [isOpen, searchQuery]);

  // Generate label based on selection
  const label = useMemo(() => {
    // Show loading state until selection is initialized (URL sync complete)
    if (!isSelectionInitialized) {
      return 'Loading...';
    }
    switch (selection.type) {
      case 'everyone':
        return 'Everyone';
      case 'current_user':
        return `${selection.user.full_name} (you)`;
      case 'specific_staff':
        return selection.staff.full_name;
    }
  }, [selection, isSelectionInitialized]);

  const isInitialLoading = isLoadingStaff && staffList.length === 0 && !localSearchQuery;
  const isSearching = isLoadingStaff && localSearchQuery !== searchQuery;
  const isLoadingMore = isLoadingStaff && staffList.length > 0 && localSearchQuery === searchQuery;
  const showEmptyState = !isLoadingStaff && staffList.length === 0 && localSearchQuery.length > 0;
  const isActiveSearch = localSearchQuery.length > 0;

  // Filter out current user from staff list when not searching (they have their own option)
  // When searching, show all results including current user
  const otherStaff = useMemo(
    () => (isActiveSearch ? staffList : staffList.filter((staff) => staff.id !== currentUser?.id)),
    [staffList, currentUser, isActiveSearch],
  );

  const renderStaffList = () => {
    if (isInitialLoading || isSearching) {
      return (
        <div className="flex flex-col justify-center items-center gap-2 py-8" data-testid="staff-filter-loading">
          <Spinner active borderWidth={0.15} color="lightgray" size={32} />
          <Label>Loading...</Label>
        </div>
      );
    }

    return (
      <div className="relative">
        <ul className="list-none m-0 px-px">
          {/* Everyone Option - hidden when searching */}
          {!isActiveSearch && (
            <li
              className={`flex items-center paragraph-2 relative px-5 py-1.5 cursor-pointer ${
                selection.type === 'everyone' ?
                  'bg-blue-100 dark:bg-blue-800 hover:bg-blue-100 dark:hover:bg-blue-800' :
                  'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid="staff-option-everyone"
              onClick={handleSelectEveryone}>
              <UserGroupIcon className="size-4 shrink-0 text-gray-400 dark:text-gray-600" />
              <span className="flex-1 ml-2 text-gray-900 dark:text-white truncate">Everyone</span>
              {selection.type === 'everyone' && (
                <CheckIcon className="size-4 shrink-0 text-blue-500 dark:text-blue-400" />
              )}
            </li>
          )}

          {/* Current User Option - hidden when searching */}
          {!isActiveSearch && currentUser && (
            <li
              className={`flex items-center paragraph-2 relative px-5 py-1.5 cursor-pointer ${
                selection.type === 'current_user' ?
                  'bg-blue-100 dark:bg-blue-800 hover:bg-blue-100 dark:hover:bg-blue-800' :
                  'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid="staff-option-current-user"
              onClick={handleSelectCurrentUser}>
              <UserCircleIcon className="size-4 shrink-0 text-gray-400 dark:text-gray-600" />
              <span className="flex-1 ml-2 text-gray-900 dark:text-white truncate">
                {currentUser.full_name} (you)
              </span>
              {selection.type === 'current_user' && (
                <CheckIcon className="size-4 shrink-0 text-blue-500 dark:text-blue-400" />
              )}
            </li>
          )}

          {/* Divider - hidden when searching */}
          {!isActiveSearch && otherStaff.length > 0 && (
            <li className="border-t border-gray-200 dark:border-gray-700 my-1" />
          )}

          {/* Other Staff Items */}
          {otherStaff.map((staff) => (
            <li
              className={`flex items-center paragraph-2 relative px-5 py-1.5 cursor-pointer ${
                selection.type === 'specific_staff' && selection.staff.id === staff.id ?
                  'bg-blue-100 dark:bg-blue-800 hover:bg-blue-100 dark:hover:bg-blue-800' :
                  'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid={`staff-option-${staff.id}`}
              key={staff.id}
              onClick={() => handleSelectStaff(staff)}>
              <UserCircleIcon className="size-4 shrink-0 text-gray-400 dark:text-gray-600" />
              <span className="flex-1 ml-2 text-gray-900 dark:text-white truncate">{staff.full_name}</span>
              {selection.type === 'specific_staff' && selection.staff.id === staff.id && (
                <CheckIcon className="size-4 shrink-0 text-blue-500 dark:text-blue-400" />
              )}
            </li>
          ))}

          {/* Empty State for search */}
          {showEmptyState && (
            <li className="flex flex-col justify-center items-center p-4" data-testid="staff-filter-empty">
              <EmptyState
                description="Try adjusting your search."
                icon={<UserCircleOutlineIcon />}
                title="No staff found" />
            </li>
          )}

          {/* Infinite Scroll Sentinel */}
          {hasMoreStaff && <div className="h-1" ref={observerTargetRef} />}

          {/* Loading More Indicator */}
          {isLoadingMore && hasMoreStaff && (
            <li className="flex flex-col justify-center items-center gap-1 py-3">
              <Spinner active borderWidth={0.15} color="lightgray" size={24} />
              <Label>Loading...</Label>
            </li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className="relative" data-testid="staff-filter" ref={containerRef}>
      <Button
        data-testid="staff-filter-button"
        disabled={!isSelectionInitialized}
        flavor="regular"
        onClick={handleToggle}
        size="small"
        taste="boundless"
        title="Filter by staff">
        <UserIcon className="size-4" />
        <span className="max-w-32 truncate">{label}</span>
        <ChevronDownIcon className="size-4" />
      </Button>

      {isOpen && (
        <Card
          className="absolute left-0 top-full mt-1 w-72 z-50 flex flex-col"
          data-testid="staff-filter-dropdown"
          zIndex="4">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <SearchInputToggle
              isLoading={isLoadingStaff}
              isToggleEnabled={false}
              onChange={handleSearchChange}
              onSubmit={(e) => e.preventDefault()}
              onToggleClick={noop}
              placeholder="Search..."
              showSubmitButton={false}
              showToggle={false}
              value={localSearchQuery} />
          </div>

          <ScrollableContainer className="max-h-64" direction="col" maskSize={3}>
            {renderStaffList()}
          </ScrollableContainer>
        </Card>
      )}
    </div>
  );
};

export default StaffFilter;
